import { API, BlockAPI, BlockTool, SanitizerConfig } from '@editorjs/editorjs';
import './index.css';

import { IconHtml } from '@codexteam/icons';

interface RawBlockData {
  html: string;
}

interface RawBlockConfig {
  placeholder: string;
}

type Constructor = {
  api: API;
  data: RawBlockData;
  config?: RawBlockConfig;
  block: BlockAPI;
  readOnly: boolean;
};

export default class RawTool implements BlockTool {
  private readonly api: API;
  private readonly data: RawBlockData;
  private readonly readOnly: boolean;
  private readonly placeholder: string;
  private textarea: HTMLTextAreaElement | null;
  private resizeDebounce: NodeJS.Timeout | null;
  private readonly CSS: {
    baseClass: string;
    input: string;
    wrapper: string;
    textarea: string;
  };

  static get isReadOnlySupported() {
    return true;
  }

  static get displayInToolbox() {
    return true;
  }

  static get enableLineBreaks() {
    return true;
  }

  static get toolbox() {
    return {
      icon: IconHtml,
      title: 'Embed',
    };
  }

  constructor({ data, config, api, readOnly }: Constructor) {
    this.api = api;
    this.data = data;
    this.readOnly = readOnly;

    this.placeholder = api.i18n.t(
      config?.placeholder || RawTool.DEFAULT_PLACEHOLDER,
    );

    this.CSS = {
      baseClass: this.api.styles.block,
      input: this.api.styles.input,
      wrapper: 'ce-rawtool',
      textarea: 'ce-rawtool__textarea',
    };

    this.data = {
      html: data.html || '',
    };

    this.textarea = null;
    this.resizeDebounce = null;
  }

  renderReadOnly() {
    const htmlContainer = document.createElement('div');
    htmlContainer.classList.add('ce-rawtool__rendered');
    htmlContainer.innerHTML = this.data.html;
    return htmlContainer;
  }

  renderEditable() {
    const textarea = document.createElement('textarea');
    textarea.classList.add(this.CSS.textarea, this.CSS.input);
    textarea.value = this.data.html;
    textarea.placeholder = this.placeholder;
    textarea.addEventListener('input', () => {
      this.onInput();
    });
    return textarea;
  }

  render() {
    const wrapper = document.createElement('div');
    wrapper.classList.add(this.CSS.baseClass, this.CSS.wrapper);

    if (this.readOnly) {
      wrapper.appendChild(this.renderReadOnly());
    } else {
      wrapper.appendChild(this.renderEditable());
      const renderingTime = 100;
      setTimeout(() => {
        this.resize();
      }, renderingTime);
    }

    return wrapper;
  }

  save(rawToolsWrapper: HTMLDivElement) {
    return {
      html: rawToolsWrapper.querySelector('textarea')?.value || '',
    };
  }

  static get DEFAULT_PLACEHOLDER() {
    return 'Enter HTML code';
  }

  static get sanitize(): SanitizerConfig {
    return {
      html: true,
      script: false,
      object: false,
      embed: false,
    };
  }

  onInput() {
    if (this.resizeDebounce) {
      clearTimeout(this.resizeDebounce);
    }

    this.resizeDebounce = setTimeout(() => {
      this.resize();
    }, 200);
  }

  resize() {
    if (!this.textarea) return;
    this.textarea.style.height = 'auto';
    this.textarea.style.height = this.textarea.scrollHeight + 'px';
  }
}
